// Dernier recours serveur (spec anti-perte phase 1, §5.2.2) : appelé quand la
// fusion a échoué deux fois sur le téléphone (`take-lifecycle.ts` →
// `{ kind: "hand_off" }`). Crée (ou met à jour, mode "complete") la ligne
// `captures` en `assembling`, envoie les segments un à un dans Storage, pose le
// marqueur de fin d'envoi en dernier, et demande au serveur d'assembler.
// Appelé par l'exécuteur des prises (A5).
import type { SupabaseClient } from "@supabase/supabase-js";
import type { TakeRecorder } from "../native/take-recorder";
import { MAX_PLAUSIBLE_ELAPSED_MS } from "./incomplete";
import { conservedMs } from "./take-lifecycle";
import { takeMeetingContext } from "./take-meta";
import type { TakeErrorInfo, TakeManifest, TakeMeta } from "./take-types";
import { AUDIO_MIME, BUCKET } from "./upload";

/** Nom de segment valide (spec §5.2, chemins Storage). */
export const SEGMENT_NAME = /^\d{4}\.m4a$/;

export interface HandOffDeps {
  client: SupabaseClient;
  recorder: TakeRecorder;
  readFile: (uri: string) => Promise<Blob>;
  isOnline: () => boolean;
}

export interface HandOffOptions {
  /**
   * "new" (défaut) : insère la ligne `captures` (dernier recours normal).
   * "complete" (C25, orphelins du 24-09) : la ligne existe déjà (capture
   * tronquée) — on la fait seulement repasser en `assembling` sans y toucher
   * autrement (son `audio_path`/`keep_audio` restent ceux du serveur).
   */
  mode?: "new" | "complete";
}

export type HandOffResult =
  | { ok: true }
  | {
      ok: false;
      reason:
        | "offline"
        | "no_project"
        | "no_capture"
        | "insert"
        | "upload"
        | "marker"
        | "invoke"
        // Le serveur n'a pas certains segments (S-m3) : ils seront renvoyés.
        | "segments_missing";
      error?: string;
    };

interface StorageLikeError {
  message: string;
  statusCode?: string;
}

// Un segment déjà présent en Storage (reprise après envoi interrompu à
// mi-chemin, C19/Review Focus 5) répond 409 (forme storage-js réelle :
// `{ statusCode: "409", error: "Duplicate", message: "The resource already
// exists" }`) : ce n'est pas un échec, la reprise doit juste marquer le
// segment comme envoyé et continuer. Détection sur `statusCode` uniquement —
// le texte de `message` n'est pas un contrat stable.
function isAlreadyUploadedError(err: StorageLikeError): boolean {
  return err.statusCode === "409";
}

function segmentStoragePath(userId: string, captureId: string, name: string): string {
  return `${userId}/${captureId}/segments/${name}`;
}

// Horloge de l'appareil modifiée pendant la prise (fuseau, réglage manuel) :
// une durée d'horloge négative ou absurde ne doit jamais être écrite en base —
// `elapsed_s >= 0` la refuserait à chaque tentative, bloquant la prise pour de
// bon. Même règle de plausibilité que `isIncomplete` (incomplete.ts), sans le
// terme de comparaison à `measuredMs` qui n'a pas sa place ici.
export function plausibleElapsedMs(elapsedMs: number | undefined): number | null {
  if (elapsedMs == null || !Number.isFinite(elapsedMs)) return null;
  if (elapsedMs <= 0 || elapsedMs > MAX_PLAUSIBLE_ELAPSED_MS) return null;
  return elapsedMs;
}

// Ligne d'abord (spec §5.2.2.1, mode "new") : l'état s'affiche sur tous les
// appareils et aucun dossier de segments n'existe sans capture. `keep_audio:
// false` + `audio_path: null` (et non `true`) : le CHECK `keep_audio_coherent`
// et le schéma Zod de l'app refusent tous deux `keep_audio:true` sans
// `audio_path` — c'est le serveur qui posera les deux ENSEMBLE à l'assemblage.
function buildAssemblingRow(
  meta: TakeMeta,
  expectedMs: number,
  elapsedS: number | null,
  captureId: string,
) {
  return {
    id: captureId,
    created_by: meta.userId,
    project_id: meta.projectId,
    type: meta.type,
    title: meta.title,
    recorded_at: meta.startedAt,
    duration_s: Math.floor(expectedMs / 1000),
    audio_path: null,
    keep_audio: false,
    status: "assembling",
    elapsed_s: elapsedS,
    // Même validation que le chemin normal : un contexte hors schéma ferait
    // refuser la ligne par Zod (capture invisible dans les listes).
    meeting_context: takeMeetingContext(meta),
    batch_extraction: meta.batchExtraction,
  };
}

// Un échec porte ce qu'il faut au journal natif (CA8) : code (statut HTTP ou
// Storage, `-1` sinon) et sous-erreur (code PostgREST, nom du segment).
type StepOutcome = { ok: true } | { ok: false; error: string; code?: number; underlying?: string };

function postgrestUnderlying(error: { code?: string }): string | undefined {
  return error.code ? `PostgREST ${error.code}` : undefined;
}

// Pose (ou met à jour) la ligne `assembling` selon le mode. "complete" ne
// touche qu'au statut, à la durée d'horloge et à `duration_s` : la ligne
// existante (capture tronquée, C25) garde son `audio_path`/`keep_audio` tels
// quels. `duration_s` est réécrit avec la somme RÉELLE des segments retrouvés
// (`expectedMs` = `conservedMs(m)`, posé par l'appelant) — sinon la fiche
// affiche « Ton enregistrement de X est en sécurité » avec l'ANCIENNE durée de
// la capture tronquée, pas celle de la réparation en cours (correctif A7 #3b).
async function upsertAssemblingRow(
  deps: HandOffDeps,
  mode: "new" | "complete",
  meta: TakeMeta,
  expectedMs: number,
  elapsedS: number | null,
  captureId: string,
): Promise<StepOutcome> {
  if (mode === "new") {
    const row = buildAssemblingRow(meta, expectedMs, elapsedS, captureId);
    // Idempotent (`ON CONFLICT (id) DO NOTHING`, même patron que uploadCapture) :
    // une reprise après interruption repasse par ici sans jamais écraser la ligne.
    const { error } = await deps.client
      .from("captures")
      .upsert(row, { onConflict: "id", ignoreDuplicates: true });
    return error
      ? { ok: false, error: error.message, underlying: postgrestUnderlying(error) }
      : { ok: true };
  }
  const { data, error } = await deps.client
    .from("captures")
    .update({
      status: "assembling",
      elapsed_s: elapsedS,
      duration_s: Math.floor(expectedMs / 1000),
    })
    .eq("id", captureId)
    .select("id");
  if (error) return { ok: false, error: error.message, underlying: postgrestUnderlying(error) };
  if (!data || data.length === 0) {
    return { ok: false, error: "capture introuvable (supprimée ou droits insuffisants)" };
  }
  return { ok: true };
}

// Envoie un segment non encore reçu (Review Focus 5 / C19, reprise) : un
// segment déjà dans `uploadedSegments` n'est jamais renvoyé. Un 409 (déjà
// présent en Storage) est traité comme un succès.
async function uploadOneSegment(
  deps: HandOffDeps,
  seg: { name: string; uri: string },
  userId: string,
  captureId: string,
): Promise<StepOutcome> {
  if (!SEGMENT_NAME.test(seg.name)) {
    return { ok: false, error: `nom de segment invalide : ${seg.name}`, underlying: seg.name };
  }
  const blob = await deps.readFile(seg.uri);
  const path = segmentStoragePath(userId, captureId, seg.name);
  const { error: uploadErr } = await deps.client.storage
    .from(BUCKET)
    .upload(path, blob, { contentType: AUDIO_MIME, upsert: false });
  if (uploadErr && !isAlreadyUploadedError(uploadErr)) {
    const status = Number(uploadErr.statusCode);
    return {
      ok: false,
      error: uploadErr.message,
      code: Number.isFinite(status) ? status : -1,
      underlying: seg.name,
    };
  }
  return { ok: true };
}

async function uploadPendingSegments(
  deps: HandOffDeps,
  m: TakeManifest,
  segments: { name: string; uri: string }[],
  userId: string,
  captureId: string,
): Promise<StepOutcome> {
  const uploaded = new Set(m.uploadedSegments);
  for (const seg of segments) {
    if (uploaded.has(seg.name)) continue;
    const outcome = await uploadOneSegment(deps, seg, userId, captureId);
    if (!outcome.ok) return outcome;
    await deps.recorder.markTake({ takeId: m.takeId, uploadedSegment: seg.name });
  }
  return { ok: true };
}

/**
 * Noms de segments que `assemble-capture` dit absents de Storage (revue finale
 * S-m3 : `{ result: { status: "skipped", reason: "segments_missing", missing } }`),
 * filtrés au patron `0001.m4a` ; `null` pour toute autre réponse.
 */
export function segmentsMissingOf(data: unknown): string[] | null {
  const result = (data as { result?: unknown } | null)?.result as
    | { status?: unknown; reason?: unknown; missing?: unknown }
    | undefined;
  if (result?.status !== "skipped" || result.reason !== "segments_missing") return null;
  if (!Array.isArray(result.missing)) return null;
  return result.missing.filter((n): n is string => typeof n === "string" && SEGMENT_NAME.test(n));
}

/**
 * Segments que le serveur n'a pas : retirés des envoyés de la prise, qui
 * repasse en envoi — le prochain passage de l'exécuteur les renvoie (seuls),
 * repose le marqueur et redemande l'assemblage. `false` si aucun nom valide.
 */
export async function forgetMissingSegments(
  recorder: Pick<TakeRecorder, "markTake">,
  takeId: string,
  missing: string[],
): Promise<boolean> {
  const names = missing.filter((n) => SEGMENT_NAME.test(n));
  if (names.length === 0) return false;
  await recorder.markTake({ takeId, state: "uploading", forgetUploadedSegments: names });
  return true;
}

// Revue finale N-I2 (CA8, volet envoi) : tout échec d'envoi ou d'appel est
// inscrit au manifeste (`lastError`) ET au journal natif joint aux
// signalements — domaine, code, message, sous-erreur. Best-effort : un natif
// qui refuse n'empêche jamais de rendre le résultat.
async function failed(
  deps: HandOffDeps,
  takeId: string,
  reason: Exclude<HandOffResult, { ok: true }>["reason"],
  report: TakeErrorInfo,
): Promise<HandOffResult> {
  await deps.recorder.markTake({ takeId, error: report }).catch(() => undefined);
  return { ok: false, reason, error: report.message };
}

function stepReport(domain: string, outcome: Exclude<StepOutcome, { ok: true }>): TakeErrorInfo {
  return {
    domain,
    code: outcome.code ?? -1,
    message: outcome.error,
    ...(outcome.underlying ? { underlying: outcome.underlying } : {}),
  };
}

// Statut HTTP d'un échec de `functions.invoke` (FunctionsHttpError.context).
function invokeStatus(error: unknown): number {
  const status = (error as { context?: { status?: unknown } } | null)?.context?.status;
  return typeof status === "number" ? status : -1;
}

// Pas de chaînage entre Edge Functions : l'app appelle `assemble-capture`
// elle-même. Un échec ici n'annule rien — le marqueur est posé, le rattrapage
// serveur (S5) prendra le relais. S-m3 : le serveur n'a pas certains segments
// marqués envoyés (objet perdu, purge) — la prise les renverra au passage suivant.
async function requestAssembly(
  deps: HandOffDeps,
  takeId: string,
  captureId: string,
): Promise<HandOffResult> {
  const { data: invokeData, error: invokeErr } = await deps.client.functions.invoke(
    "assemble-capture",
    { body: { capture_id: captureId } },
  );
  if (invokeErr) {
    return failed(deps, takeId, "invoke", {
      domain: "Supabase.functions.assemble-capture",
      code: invokeStatus(invokeErr),
      message: invokeErr.message,
    });
  }
  const missing = segmentsMissingOf(invokeData) ?? [];
  if (await forgetMissingSegments(deps.recorder, takeId, missing)) {
    return failed(deps, takeId, "segments_missing", {
      domain: "assemble-capture",
      code: -1,
      message: "segments absents du serveur",
      underlying: missing.join(","),
    });
  }
  return { ok: true };
}

export async function handOffToServer(
  deps: HandOffDeps,
  m: TakeManifest,
  meta: TakeMeta,
  opts: HandOffOptions = {},
): Promise<HandOffResult> {
  // C20 : hors ligne — aucun appel réseau, l'état reste « En préparation ».
  if (!deps.isOnline()) return { ok: false, reason: "offline" };
  // C13 : métadonnées sans projet — `!meta.projectId` couvre `null` ET
  // `undefined` (méta JSON écrite sans la clé), pas seulement `=== null`.
  if (!meta.projectId) return { ok: false, reason: "no_project" };

  // Posé par `TakeRecorder.startTake` au démarrage de la prise (contrat 0.4) —
  // absent seulement pour une prise adoptée depuis les orphelins
  // (`adoptOrphans`, §5.2.4) : l'exécuteur (A5) posera un `captureId` puis
  // rappellera, jamais une exception ici.
  if (m.captureId === undefined) return { ok: false, reason: "no_capture" };
  const captureId = m.captureId;
  const mode = opts.mode ?? "new";

  // `handOffToServer` ne lève jamais (A5 en dépend) : un `readFile`, `markTake`
  // ou `getTakeSegments` qui rejette (natif indisponible, disque, etc.) devient
  // un résultat `{ ok:false, reason:"upload" }` au lieu d'une exception non
  // gérée.
  try {
    await deps.recorder.markTake({ takeId: m.takeId, state: "uploading", captureId });

    const expectedMs = conservedMs(m);
    const elapsedMs = plausibleElapsedMs(m.elapsedMs);
    const elapsedS = elapsedMs != null ? Math.floor(elapsedMs / 1000) : null;

    const rowOutcome = await upsertAssemblingRow(deps, mode, meta, expectedMs, elapsedS, captureId);
    if (!rowOutcome.ok) {
      const domain = mode === "new" ? "Supabase.captures.insert" : "Supabase.captures.update";
      return failed(deps, m.takeId, "insert", stepReport(domain, rowOutcome));
    }

    const { segments } = await deps.recorder.getTakeSegments(m.takeId);
    const uploadOutcome = await uploadPendingSegments(deps, m, segments, meta.userId, captureId);
    if (!uploadOutcome.ok) {
      return failed(deps, m.takeId, "upload", stepReport("Supabase.storage.upload", uploadOutcome));
    }

    // Marqueur en dernier (Review Focus 5) : sans lui, `assemble-capture` ne
    // fait rien — un envoi interrompu avant ce point sera repris depuis le
    // début de cette même fonction, sans jamais assembler une prise
    // partiellement reçue.
    const { error: markerErr } = await deps.client
      .from("captures")
      .update({
        assembly_manifest: {
          names: segments.map((seg) => seg.name),
          expectedMs,
          elapsedMs,
        },
      })
      .eq("id", captureId);
    if (markerErr) {
      return failed(deps, m.takeId, "marker", {
        domain: "Supabase.captures.marker",
        code: -1,
        message: markerErr.message,
        ...(markerErr.code ? { underlying: `PostgREST ${markerErr.code}` } : {}),
      });
    }

    await deps.recorder.markTake({ takeId: m.takeId, state: "handed_to_server" });
    return await requestAssembly(deps, m.takeId, captureId);
  } catch (err) {
    return failed(deps, m.takeId, "upload", {
      domain: "Ecoute.handOff",
      code: -1,
      message: err instanceof Error ? err.message : String(err),
    });
  }
}
